import React from 'react';
import { mockAttendance, mockEmployees, mockLeaves } from '../mockData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Calendar, CheckCircle, XCircle, Clock, Download } from 'lucide-react';

const Attendance = () => {
  const totalPresent = mockAttendance.reduce((sum, att) => sum + att.present, 0);
  const totalAbsent = mockAttendance.reduce((sum, att) => sum + att.absent, 0);
  const totalLeaves = mockAttendance.reduce((sum, att) => sum + att.leaves, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Leaves Management</h1>
          <p className="text-gray-500 mt-1">Track employee leaves and attendance</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700">
          <Download className="mr-2 h-4 w-4" />
          Export Report
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Present</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{totalPresent}</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Leaves</p>
                <p className="text-2xl font-bold text-orange-600 mt-1">{totalLeaves}</p>
              </div>
              <div className="bg-orange-50 p-3 rounded-lg">
                <Calendar className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Absent</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{totalAbsent}</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Attendance Rate</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">96%</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="attendance" className="w-full">
        <TabsList>
          <TabsTrigger value="attendance">Monthly Attendance</TabsTrigger>
          <TabsTrigger value="leaves">Leave Requests</TabsTrigger>
        </TabsList>

        <TabsContent value="attendance" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>December 2024 Attendance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Present Days</TableHead>
                      <TableHead>Absent Days</TableHead>
                      <TableHead>Leave Days</TableHead>
                      <TableHead>Weekly Offs</TableHead>
                      <TableHead>Holidays</TableHead>
                      <TableHead>Attendance %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mockAttendance.map((att) => {
                      const employee = mockEmployees.find((e) => e.id === att.employeeId);
                      const totalWorkingDays = att.present + att.absent + att.leaves;
                      const attendancePercentage = Math.round((att.present / totalWorkingDays) * 100);
                      
                      return (
                        <TableRow key={att.employeeId}>
                          <TableCell>
                            <div className="flex items-center space-x-3">
                              <img
                                src={employee?.photo}
                                alt={employee?.name}
                                className="w-10 h-10 rounded-full"
                              />
                              <div>
                                <p className="font-medium">{employee?.name}</p>
                                <p className="text-sm text-gray-500">{employee?.designation}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-green-600">{att.present}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="destructive">{att.absent}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-orange-600 border-orange-600">
                              {att.leaves}
                            </Badge>
                          </TableCell>
                          <TableCell>{att.weeklyOffs}</TableCell>
                          <TableCell>{att.holidays}</TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <div className="w-full bg-gray-200 rounded-full h-2 max-w-[100px]">
                                <div
                                  className="bg-green-600 h-2 rounded-full"
                                  style={{ width: `${attendancePercentage}%` }}
                                ></div>
                              </div>
                              <span className="font-medium text-sm">{attendancePercentage}%</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leaves" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Leave Requests</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Leave Type</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>End Date</TableHead>
                      <TableHead>Days</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mockLeaves.map((leave) => (
                      <TableRow key={leave.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{leave.employeeName}</p>
                            <p className="text-sm text-gray-500">{leave.employeeId}</p>
                          </div>
                        </TableCell>
                        <TableCell>{leave.type}</TableCell>
                        <TableCell>{leave.startDate}</TableCell>
                        <TableCell>{leave.endDate}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{leave.days} days</Badge>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">{leave.reason}</TableCell>
                        <TableCell>
                          <Badge
                            className={
                              leave.status === 'approved'
                                ? 'bg-green-600'
                                : leave.status === 'pending'
                                ? 'bg-orange-500'
                                : 'bg-red-600'
                            }
                          >
                            {leave.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {leave.status === 'pending' && (
                            <div className="flex space-x-2">
                              <Button size="sm" variant="outline" className="text-green-600 border-green-600">
                                Approve
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-600">
                                Reject
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Attendance;